import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Badge } from "../components/ui/Badge";
import { Plus, Search, Edit, Trash2, Calendar, Globe, FileText, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { pageService } from "../services/pages";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/AlertDialog";

interface Page {
  id: number;
  title: string;
  slug: string;
  active: boolean;
  updated_at: string;
  subtitle?: string;
  content?: string;
  meta_title?: string;
  meta_description?: string;
}

export function PaginasPage() {
  const [pages, setPages] = useState<Page[]>([]);
  const [metadata, setMetadata] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  const fetchPages = async (page = 1) => {
    try {
      setLoading(true);
      const response = await pageService.getAllPages({ page });
      if (response.data && response.data.success) {
        setPages(response.data.data);
        setMetadata(response.data.meta);
      }
    } catch (err: any) {
      console.error('Error fetching pages:', err);
      setError('Erro ao carregar as páginas.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPages(currentPage);
  }, [currentPage]);

  const filteredPages = pages.filter(page =>
    page.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    page.slug.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEdit = (page: Page) => {
    navigate(`/editar-pagina/${page.id}`);
  };

  const handleDelete = async (id: number) => {
    try {
      await pageService.deletePage(id);
      fetchPages();
    } catch (err) {
      console.error("Erro ao excluir a página", err);
      alert("Erro ao excluir a página.");
    }
  };

  return (
    <main className="flex-1 p-6 bg-gray-50">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#68686A]">Páginas</h1>
        <p className="text-[#68686A]">Gerencie as páginas estáticas do site</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <Card className="border border-gray-200 hover:shadow-md transition-shadow duration-200">
          <CardHeader>
            <CardTitle className="text-[#68686A]">Total de Páginas</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold text-gray-900">{metadata?.total || 0}</p>
            <p className="text-sm text-[#68686A]">Páginas criadas</p>
          </CardContent>
        </Card>

        <Card className="border border-gray-200 hover:shadow-md transition-shadow duration-200">
          <CardHeader>
            <CardTitle className="text-[#68686A]">Publicadas</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold text-gray-900">{metadata?.published_count || 0}</p>
            <p className="text-sm text-[#849D70]">Visíveis ao público</p>
          </CardContent>
        </Card>

        <Card className="border border-gray-200 hover:shadow-md transition-shadow duration-200">
          <CardHeader>
            <CardTitle className="text-[#68686A]">Rascunhos</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold text-gray-900">
              {metadata?.draft_count || 0}
            </p>
            <p className="text-sm text-[#D4C070]">Em desenvolvimento</p>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Buscar páginas..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 border-gray-300 focus:ring-2 focus:ring-[#D4C070] focus:border-transparent"
          />
        </div>
        <Button
          onClick={() => navigate("/nova-pagina")}
          className="bg-[#D4C070] hover:bg-[#849D70] text-white"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nova Página
        </Button>
      </div>

      <Card className="mb-6 border border-gray-200 hover:shadow-md transition-shadow duration-200">
        <CardHeader>
          <CardTitle className="text-[#68686A]">Lista de Páginas</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-[#D4C070]" />
            </div>
          ) : error ? (
            <div className="text-center text-red-500 py-4">{error}</div>
          ) : filteredPages.length === 0 ? (
            <div className="text-center text-gray-500 py-4">Nenhuma página encontrada.</div>
          ) : (
            <>
              {/* Versão Desktop - Tabela */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 text-[#68686A]">Título</th>
                      <th className="text-left py-3 px-4 text-[#68686A]">URL</th>
                      <th className="text-left py-3 px-4 text-[#68686A]">Status</th>
                      <th className="text-left py-3 px-4 text-[#68686A]">Última Modificação</th>
                      <th className="text-left py-3 px-4 text-[#68686A]">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPages.map((page) => (
                      <tr key={page.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-[#68686A]" />
                            <span className="font-medium text-gray-900">{page.title}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <a
                            href={`${import.meta.env.VITE_STORE_URL}/institucional/${page.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-sm text-[#D4C070] hover:underline cursor-pointer"
                          >
                            <Globe className="h-3 w-3" />
                            <span>{page.slug}</span>
                          </a>
                        </td>
                        <td className="py-3 px-4">
                          <Badge
                            variant={page.active ? 'default' : 'secondary'}
                            className={
                              page.active ? 'bg-[#849D70] text-white border-[#849D70]' : 'bg-[#D4C070] text-white border-[#D4C070]'
                            }
                          >
                            {page.active ? 'Publicado' : 'Rascunho'}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-sm text-[#68686A]">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {page.updated_at ? new Date(page.updated_at).toLocaleDateString('pt-BR') : '-'}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-[#68686A] hover:bg-[#EEF1F2] hover:text-[#D4C070] border-gray-300"
                              onClick={() => handleEdit(page)}
                            >
                              <Edit className="h-3 w-3" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-[#68686A] hover:bg-red-50 hover:text-red-500 border-gray-300"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Tem certeza que deseja excluir esta página?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Esta ação não pode ser desfeita. Isso excluirá permanentemente a página.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDelete(page.id)}
                                    className="bg-red-500 hover:bg-red-600 focus:ring-red-500"
                                  >
                                    Excluir
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Versão Mobile - Cards */}
              <div className="md:hidden space-y-4">
                {filteredPages.map((page) => (
                  <Card key={page.id} className="border border-gray-200">
                    <CardContent className="p-4 space-y-3">
                      {/* Título e Status */}
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-start gap-2 flex-1">
                          <FileText className="h-4 w-4 text-[#68686A] mt-0.5 flex-shrink-0" />
                          <span className="font-medium text-gray-900">{page.title}</span>
                        </div>
                        <Badge
                          variant={page.active ? 'default' : 'secondary'}
                          className={
                            page.active ? 'bg-[#849D70] text-white border-[#849D70] flex-shrink-0' : 'bg-[#D4C070] text-white border-[#D4C070] flex-shrink-0'
                          }
                        >
                          {page.active ? 'Publicado' : 'Rascunho'}
                        </Badge>
                      </div>

                      {/* URL/Slug */}
                      <a
                        href={`${import.meta.env.VITE_STORE_URL}/institucional/${page.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-sm text-[#D4C070] hover:underline cursor-pointer"
                      >
                        <Globe className="h-3 w-3" />
                        <span className="truncate">{page.slug}</span>
                      </a>

                      {/* Data de Modificação */}
                      <div className="flex items-center gap-1 text-sm text-[#68686A]">
                        <Calendar className="h-3 w-3 flex-shrink-0" />
                        {page.updated_at ? new Date(page.updated_at).toLocaleDateString('pt-BR') : '-'}
                      </div>

                      {/* Botões de Ação */}
                      <div className="flex gap-2 pt-2 border-t">
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 text-[#68686A] hover:bg-[#EEF1F2] hover:text-[#D4C070] border-gray-300"
                          onClick={() => handleEdit(page)}
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Editar
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:bg-red-50 hover:border-red-300 border-gray-300"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Tem certeza que deseja excluir esta página?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Esta ação não pode ser desfeita. Isso excluirá permanentemente a página.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDelete(page.id)}
                                className="bg-red-500 hover:bg-red-600 focus:ring-red-500"
                              >
                                Excluir
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {metadata && metadata.last_page > 1 && (
                <div className="mt-6 flex flex-col items-center gap-3 border-t pt-4">
                  <p className="text-sm text-gray-500">
                    Mostrando <span className="font-medium">{(currentPage - 1) * 15 + 1}</span> até{' '}
                    <span className="font-medium">{Math.min(currentPage * 15, metadata.total)}</span> de{' '}
                    <span className="font-medium">{metadata.total}</span> resultados
                  </p>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="border-gray-300 text-[#68686A] hover:bg-[#EEF1F2] disabled:opacity-50"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    {Array.from({ length: Math.min(5, metadata.last_page) }, (_, i) => {
                      let pageNum;
                      if (metadata.last_page <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= metadata.last_page - 2) {
                        pageNum = metadata.last_page - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      return (
                        <Button
                          key={pageNum}
                          variant={currentPage === pageNum ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setCurrentPage(pageNum)}
                          className={
                            currentPage === pageNum
                              ? 'bg-[#D4C070] hover:bg-[#849D70] text-white'
                              : 'border-gray-300 text-[#68686A] hover:bg-[#EEF1F2]'
                          }
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === metadata.last_page}
                      className="border-gray-300 text-[#68686A] hover:bg-[#EEF1F2] disabled:opacity-50"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </main>
  );
}
export default PaginasPage;
